/*
 * SMParts — Hero-Slider
 *
 * Das Wischen macht CSS (scroll-snap). Hier steht nur, was CSS nicht
 * kann: Pfeile und Punkte auf den Stand der Spur bringen.
 *
 * Die aktuelle Folie wird aus scrollLeft / clientWidth berechnet und
 * nicht aus einem Ereignis mitgezaehlt. Wischen, Pfeilklick und
 * Punktklick laufen so ueber denselben Weg — mitgezaehlte Staende
 * laufen frueher oder spaeter auseinander.
 *
 * Bewusst NICHT enthalten: ein Waechter, der quere Mausrad-Gesten
 * abfaengt. Der hing hier bis 26.08.2026 drin und war ein Fehler —
 * Chrome haengt eine Scroll-Geste an das Element, auf dem sie beginnt.
 * Wurden die ersten Ereignisse abgefangen, war die ganze Geste tot und
 * die Seite liess sich nicht mehr scrollen. Dass die Spur nie zwischen
 * zwei Bildern stehen bleibt, erledigt scroll-snap-type: mandatory.
 */
class SMPHeroSlider extends HTMLElement {
  connectedCallback() {
    /*
     * connectedCallback laeuft erneut, sobald das Element im DOM
     * verschoben wird — im Theme-Editor passiert das bei jedem
     * Umsortieren. Ohne diese Sperre haengen dann zwei, drei Saetze
     * Ereignisse am selben Knopf und ein Klick springt zwei Folien weit.
     */
    if (this.bereit) return;

    this.spur = this.querySelector('.smp-hs__spur');
    if (!this.spur) return;

    this.bereit = true;
    this.folien = this.querySelectorAll('.smp-hs__folie');
    this.pfeile = this.querySelectorAll('.smp-hs__pfeil');
    this.punkte = this.querySelectorAll('.smp-hs__punkt');

    this.pfeile.forEach((knopf) => {
      knopf.addEventListener('click', () => {
        this.springe(this.aktuelle() + Number(knopf.dataset.richtung));
      });
    });

    this.punkte.forEach((punkt) => {
      punkt.addEventListener('click', () => {
        this.springe(Number(punkt.dataset.index));
      });
    });

    /*
     * scroll feuert waehrend des Wischens dutzendfach. Ohne Drosselung
     * laeuft die Aktualisierung in jedem Frame und macht das Wischen auf
     * schwachen Geraeten ruckelig.
     *
     * Bewusst setTimeout statt requestAnimationFrame: liegt der Tab im
     * Hintergrund, feuert rAF nicht mehr. Die Sperre bliebe dann fuer
     * immer gesetzt und Pfeile und Punkte wuerden nie wieder
     * nachgefuehrt. Zehnmal je Sekunde reicht fuer vier Punkte.
     */
    let sperre = null;
    this.spur.addEventListener('scroll', () => {
      if (sperre) return;
      sperre = window.setTimeout(() => {
        sperre = null;
        this.aktualisiere();
      }, 100);
    });

    /*
     * Beim Drehen des Geraets aendert sich die Folienbreite. Der
     * Scrollstand bleibt aber in Pixeln stehen und zeigt danach mitten
     * zwischen zwei Bildern. Also auf die neue Breite umrechnen.
     */
    let dreh = null;
    window.addEventListener('resize', () => {
      window.clearTimeout(dreh);
      const folie = this.aktuelle();
      dreh = window.setTimeout(() => {
        this.spur.scrollLeft = folie * this.spur.clientWidth;
        this.aktualisiere();
      }, 150);
    });

    this.aktualisiere();
  }

  aktuelle() {
    /*
     * clientWidth ist 0, solange die Section ausgeblendet ist — die
     * Division ergaebe Infinity und aktualisiere() setzte danach
     * unsinnige Zustaende.
     */
    const breite = this.spur.clientWidth;
    if (!breite) return 0;
    return Math.round(this.spur.scrollLeft / breite);
  }

  springe(index) {
    const letzte = this.folien.length - 1;
    const ziel = Math.max(0, Math.min(index, letzte));
    const links = ziel * this.spur.clientWidth;
    const sanft = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.spur.scrollTo({ left: links, behavior: sanft ? 'smooth' : 'auto' });

    if (!sanft) {
      this.aktualisiere();
      return;
    }

    /*
     * Netz fuer den Fall, dass die weiche Bewegung gar nicht anlaeuft —
     * etwa wenn der Tab im Hintergrund liegt oder der Browser die
     * Animation abbricht. Ohne das Netz bliebe der Pfeil ohne Wirkung.
     *
     * Der Vergleich laeuft ueber einen Toleranzwert und nicht ueber
     * Gleichheit: waehrend einer weichen Bewegung ist scrollLeft ein
     * Kommawert, und der trifft den Startwert nie exakt.
     */
    window.clearTimeout(this.netz);
    const vorher = this.spur.scrollLeft;
    this.netz = window.setTimeout(() => {
      const steht = Math.abs(this.spur.scrollLeft - vorher) < 1;
      if (steht && Math.abs(vorher - links) >= 1) {
        this.spur.scrollLeft = links;
      }
      this.aktualisiere();
    }, 250);
  }

  aktualisiere() {
    const jetzt = this.aktuelle();
    const letzte = this.folien.length - 1;

    this.pfeile.forEach((knopf) => {
      const ziel = jetzt + Number(knopf.dataset.richtung);
      const aus = ziel < 0 || ziel > letzte;

      /*
       * aria-disabled statt disabled. Ein disabled-Knopf verliert den
       * Tastaturfokus in dem Moment, in dem er gesperrt wird — wer sich
       * mit der Tastatur bis zur letzten Folie klickt, landet dadurch
       * wieder ganz oben im Dokument. Der Klick wird stattdessen in
       * springe() ohnehin auf den gueltigen Bereich begrenzt.
       */
      knopf.setAttribute('aria-disabled', aus ? 'true' : 'false');
    });

    this.punkte.forEach((punkt) => {
      punkt.setAttribute(
        'aria-selected',
        Number(punkt.dataset.index) === jetzt ? 'true' : 'false'
      );
    });
  }
}

if (!customElements.get('smp-hero-slider')) {
  customElements.define('smp-hero-slider', SMPHeroSlider);
}
